/* The smallest height this page will ask its host for. Below it the map — the reason the
   section exists — becomes a strip, and idle content alone measures 172px. It must stay in
   step with the `min-h-[max(460px,100dvh)]` the layout paints with; Tailwind needs that as
   a literal, which is why the number lives in two places. */
export const EMBED_MIN_HEIGHT_PX = 460;

type Measurement = {
  /* Top of the layout root, in viewport coordinates. */
  rootTop: number;
  /* Bottom of the element that wraps the panel's own children. It is content-sized, which
     is what makes this measurement safe to run in response to its own effect. */
  contentBottom: number;
  /* The panel's bottom padding, which the content wrapper does not include. */
  paddingBottom: number;
  /* Whether the panel floats over the map instead of sitting under it. Read from the
     panel's computed position, so it follows the stylesheet rather than a second copy of
     the breakpoint in JavaScript. */
  floating: boolean;
};

/* How tall this page actually needs to be, told to the host so it can size the iframe.

   Read off the CONTENT, never off the viewport. The host acts on each report by resizing
   the frame, which resizes the viewport, which fires the observer again: a viewport-derived
   number would climb on every pass and the section would never shrink back. The content's
   bottom against the root's top is stable under that loop, and covers both arrangements
   with one measurement — in a row the panel starts at the root's top, stacked it starts
   below the map, and the map's height falls out of the subtraction either way. */
export const neededHeight = ({
  rootTop,
  contentBottom,
  paddingBottom,
  floating,
}: Measurement): number => {
  /* A floating panel is a card over the map that scrolls inside itself, so the page needs
     no more height than the map does. Measuring it would be worse than useless: the card is
     capped at the frame's height, so its bottom tracks the frame, and reporting that would
     grow the frame on every pass — the loop this whole measurement exists to avoid. */
  if (floating) {
    return EMBED_MIN_HEIGHT_PX;
  }
  return Math.max(EMBED_MIN_HEIGHT_PX, Math.round(contentBottom - rootTop + paddingBottom));
};
